import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormApi, useFormState } from 'informed';
import { GroupBase, OptionsOrGroups } from 'react-select';
import { t } from 'i18next';
import cn from 'classnames';
import { Dropdown } from '@ui-modules/informed';
import { IDropdownOption } from '@ui-modules/types';
import { useRepository } from '@context';
import { notEmptyValidator } from '@common/utils';
import { MultiSteps } from '@common/interfaces';
import { useNonSpammyFieldState } from '@common/hooks';
import css from '../styles.module.scss';

interface GuestAgencyFieldProps {
  agencyId: string;
  agencyName: string;
}

const GuestAgencyField: FC<GuestAgencyFieldProps> = ({ agencyName, agencyId }) => {
  const formApi = useFormApi();
  const { agenciesRepository } = useRepository();
  const { values } = useFormState() as any;
  const { value: noPax } = useNonSpammyFieldState<boolean>('no_passengers');

  const [selectedValue, setSelectedValue] = useState<IDropdownOption | undefined>();

  const loadAgencyOptions = async (
    short_name: string,
    loadedOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
  ) => {
    const response = await agenciesRepository.getAgencies({
      short_name,
      offset: loadedOptions.length,
    });

    return {
      options: response.results.map((agency) => ({
        label: agency.short_name,
        value: agency.uuid,
      })),
      hasMore: Boolean(response.next),
    };
  };

  useEffect(() => {
    if (noPax) {
      formApi.setValue(`${MultiSteps.MainInfo}.agency`, { label: agencyName, value: agencyId });
      setSelectedValue({ label: agencyName, value: agencyId });
      return;
    }

    if (values?.agenciesOptions?.length === 1) {
      const [agencyValue] = values.agenciesOptions;
      formApi.setValue(`${MultiSteps.MainInfo}.agency`, agencyValue);

      if (agencyValue.isThirdParty) {
        toast.warn('Please make sure that the email entered is correct');
      }
    }
  }, [agencyName, noPax, values.agenciesOptions]);

  return (
    <div className={css.field}>
      {noPax ? (
        <Dropdown
          asyncPaginate
          className={css.field}
          label={t('common.agency')}
          labelClassName={cn('dark-border', 'small-list')}
          loadOptions={loadAgencyOptions}
          name="agency"
          validate={notEmptyValidator}
          value={selectedValue}
          onChange={setSelectedValue}
          data-testid="mobility-planner-edit-form-agency"
        />
      ) : (
        <Dropdown
          className={css.field}
          label={t('common.agency')}
          labelClassName={cn('dark-border', 'small-list')}
          placeholder="Provide user email first"
          name="agency"
          options={values?.agenciesOptions || []}
          validate={notEmptyValidator}
          data-testid="mobility-planner-edit-form-agency"
        />
      )}
    </div>
  );
};

export default GuestAgencyField;
